import React from 'react';
import { Plus, Trash2, Users, X, UserPlus, Info, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const EXERCISE_ROLES = [
  'Exercise Director',
  'Controller',
  'Evaluator',
  'Observer',
  'Participant',
  'Support Team',
  'Approver',
  'External Partner',
];

const emptyEntity = () => ({
  department_unit_id: '',
  type: 'internal',
  contact_persons: [], // [{ id, name }]
  role: '',
  notes: '',
});

const userLabel = (u) => u?.name || u?.full_name || u?.user_account || u?.email || u?.id || '';

// Resolve a contact's display name; fall back to a users lookup when only an id is stored
const resolveContactName = (contact, users = []) => {
  if (!contact) return '';
  if (contact.name && contact.name !== contact.id) return contact.name;
  const u = users.find((x) => x.id === contact.id);
  if (u) return userLabel(u);
  return contact.name || contact.id || '';
};

const uniqueById = (list) => {
  const seen = new Set();
  return list.filter((x) => {
    const key = x?.id || x?.name;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Derive department leader(s) as contact entries from a department object
const getDepartmentLeaders = (dept, users = []) => {
  if (!dept) return [];
  const result = [];
  // 1) members flagged as leader
  (dept.members || []).forEach((m) => {
    if (m?.is_leader) {
      const matched = users.find((u) => u.id === m.id || u.id === m.user_id);
      result.push({ id: m.id || m.user_id || m.name, name: m.name || userLabel(matched) });
    }
  });
  // 2) leaders array of user ids
  (dept.leaders || []).forEach((uid) => {
    const id = typeof uid === 'string' ? uid : uid?.id;
    if (!id) return;
    const u = users.find((x) => x.id === id);
    result.push({ id, name: u ? userLabel(u) : (typeof uid === 'object' ? uid.name : id) });
  });
  // 3) department_leader name fallback
  if (result.length === 0 && dept.department_leader) {
    result.push({ id: dept.department_leader, name: dept.department_leader });
  }
  return uniqueById(result);
};

/**
 * Organizational Scope (Departments / Units) section for the Exercise form.
 * Each row represents a participating department/unit (or external entity) with its
 * role and one or more contact persons. Contact persons are auto-pulled from the
 * selected department's leader(s) and can be supplemented with any app user.
 */
const ParticipatingEntities = ({
  value = [],
  onChange,
  departments = [],
  users = [],
  teams = [],
}) => {
  const entities = Array.isArray(value) ? value : [];

  const crews = React.useMemo(() => {
    const list = [];
    (teams || []).forEach((t) => {
      (t.crews || []).forEach((c) => {
        if (c?.id) list.push({ id: c.id, name: c.crew_name || c.name || 'Crew', team: t.team_name || '' });
      });
    });
    return list;
  }, [teams]);

  const updateRow = (index, field, fieldValue) => {
    const next = entities.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, [field]: fieldValue };
      // When department changes, auto-pull leader(s) as contact persons
      if (field === 'department_unit_id') {
        const dept = departments.find((d) => d.id === fieldValue);
        updated.contact_persons = getDepartmentLeaders(dept, users).map((c) => ({ ...c, type: 'user' }));
      }
      return updated;
    });
    onChange(next);
  };

  // Add a contact of type user | crew | department from the picker value `${type}:${id}`
  const addContact = (index, comboValue) => {
    if (!comboValue) return;
    const [type, id] = comboValue.split(':');
    if (!type || !id) return;
    let name = id;
    if (type === 'user') {
      const u = users.find((x) => x.id === id);
      if (!u) return;
      name = userLabel(u);
    } else if (type === 'crew') {
      const c = crews.find((x) => x.id === id);
      name = c ? c.name : id;
    } else if (type === 'department') {
      const d = departments.find((x) => x.id === id);
      name = d ? (d.department_name || d.name || id) : id;
    }
    const next = entities.map((row, i) => {
      if (i !== index) return row;
      const current = Array.isArray(row.contact_persons) ? row.contact_persons : [];
      if (current.some((c) => c.id === id && (c.type || 'user') === type)) return row;
      return { ...row, contact_persons: [...current, { id, name, type }] };
    });
    onChange(next);
  };

  const removeContact = (index, contactId) => {
    const next = entities.map((row, i) => {
      if (i !== index) return row;
      return {
        ...row,
        contact_persons: (row.contact_persons || []).filter((c) => c.id !== contactId),
      };
    });
    onChange(next);
  };

  const addRow = () => onChange([...entities, emptyEntity()]);
  const removeRow = (index) => onChange(entities.filter((_, i) => i !== index));

  const selectCls =
    'w-full h-9 px-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400';

  return (
    <div className="space-y-4 border-b pb-6">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-600" />
          Plan Discussion Group
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-gray-400 hover:text-emerald-600" data-testid="discussion-group-info">
                  <Info className="h-3.5 w-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                Add anyone you want to discuss the plan with. The system also auto-recommends related departments based on the exercise's selected location.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addRow}
          className="text-emerald-600 hover:text-emerald-700 h-8 px-2"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Entity
        </Button>
      </div>

      {entities.length === 0 && (
        <p className="text-xs text-gray-400 italic">
          No one added yet. Click Add Entity to choose departments, crews or users to discuss the plan — related departments are auto-recommended from the location.
        </p>
      )}

      <div className="space-y-4">
        {entities.map((row, index) => {
          const contacts = Array.isArray(row.contact_persons) ? row.contact_persons : [];
          return (
            <div
              key={index}
              className={`relative bg-emerald-50/40 border border-emerald-100 rounded-lg p-4 ${row.recommended ? 'pt-8' : ''}`}
            >
              {row.recommended && (
                <span
                  data-testid="entity-recommended-chip"
                  className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200"
                >
                  <Sparkles className="h-2.5 w-2.5" /> Recommended
                </span>
              )}
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1"
                title="Remove entity"
              >
                <Trash2 className="h-4 w-4" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Department / Unit (first, always shown) */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">
                    Department / Unit
                  </label>
                  <select
                    value={row.department_unit_id || ''}
                    onChange={(e) => updateRow(index, 'department_unit_id', e.target.value)}
                    className={selectCls}
                  >
                    <option value="">Select department / unit</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.department_name || d.name || d.id}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Internal / External */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">
                    Internal / External
                  </label>
                  <select
                    value={row.type || 'internal'}
                    onChange={(e) => updateRow(index, 'type', e.target.value)}
                    className={selectCls}
                  >
                    <option value="internal">Internal</option>
                    <option value="external">External</option>
                  </select>
                </div>

                {/* Contact Persons (multiple, auto-pulled from dept leader + add any) */}
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">
                    Contact Persons
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {contacts.length === 0 && (
                      <span className="text-xs text-gray-400 italic">No contacts. Select a department to auto-fill its leader, or add a department/crew/user below.</span>
                    )}
                    {contacts.map((c) => (
                      <span
                        key={`${c.type || 'user'}:${c.id}`}
                        className="inline-flex items-center gap-1 bg-white border border-emerald-200 text-emerald-800 text-xs rounded-full px-2 py-1"
                      >
                        {(c.type && c.type !== 'user') && (
                          <span className="text-[8px] font-bold uppercase text-emerald-500">{c.type}</span>
                        )}
                        {resolveContactName(c, users)}
                        <button
                          type="button"
                          onClick={() => removeContact(index, c.id)}
                          className="text-emerald-500 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-emerald-600" />
                    <select
                      value=""
                      onChange={(e) => addContact(index, e.target.value)}
                      className={selectCls}
                      data-testid="add-contact-select"
                    >
                      <option value="">Add department / crew / user...</option>
                      <optgroup label="Departments">
                        {departments.map((d) => (
                          <option key={`department:${d.id}`} value={`department:${d.id}`}>
                            {d.department_name || d.name || d.id}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Crews">
                        {crews.map((c) => (
                          <option key={`crew:${c.id}`} value={`crew:${c.id}`}>
                            {c.team ? `${c.name} · ${c.team}` : c.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Users">
                        {users.map((u) => (
                          <option key={`user:${u.id}`} value={`user:${u.id}`}>
                            {userLabel(u)}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                {/* Role in the Exercise */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">
                    Role in the Exercise
                  </label>
                  <select
                    value={row.role || ''}
                    onChange={(e) => updateRow(index, 'role', e.target.value)}
                    className={selectCls}
                  >
                    <option value="">Select role</option>
                    {EXERCISE_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">
                    Notes
                  </label>
                  <Input
                    value={row.notes || ''}
                    onChange={(e) => updateRow(index, 'notes', e.target.value)}
                    placeholder="Additional notes about this entity / role"
                    className="bg-white h-9"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ParticipatingEntities;
